import type { AnteprimaImport, EsitoImport, Iscritto, Webinar } from '@aulaviva/shared';
import { Alert, Badge, Button, Card, DataTable, Dialog, EmptyState, FileDrop, NativeSelect, Outcome, TabPanel, Tabs, TextField, useToast, type ColumnDef } from '@aulaviva/ui';
import { CalendarPlus, CircleCheck, CircleDashed, Download, Flag, Link2, Pencil, Plus, Send, UserPlus, Users } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import {
  useAggiungiIscritto,
  useAnnullaImport,
  useAnnullaIscrizione,
  useCambiaStatoPercorso,
  useCaricaImport,
  useConfermaImport,
  useIscritti,
  usePercorsoAdmin,
  useWebinarAdmin,
} from '@/api/admin';
import { formattaDataBreve, formattaIntervalloBreve } from '@/lib/date';
import { admin, amministrazione, comuni } from '@/testi';
import { urlEsportaIscritti } from '@/api/amministrazione';
import { BadgePercorso, BadgeWebinar, BottoneCopia, CampoRicerca, IntestazioneAdmin, LinkIndietro } from './comuni';

export function PercorsoDettaglio() {
  const t = admin.percorsi.dettaglio;
  const { id = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const scheda = params.get('scheda') ?? 'webinar';
  const percorso = usePercorsoAdmin(id);
  const stato = useCambiaStatoPercorso(id);
  const toast = useToast();
  const navigate = useNavigate();
  const [dialogo, setDialogo] = useState<'pubblica' | 'chiudi' | null>(null);

  if (percorso.isPending) return <p className="t-body text-ink-muted">{comuni.caricamento}</p>;
  if (percorso.isError) return <Alert variant="error" title={percorso.error.message} action={<Button onClick={() => percorso.refetch()}>{comuni.riprova}</Button>} />;
  const p = percorso.data;
  const linkPubblico = `${window.location.origin}/registrati?percorso=${p.id}`;

  async function cambiaStato(azione: 'pubblica' | 'chiudi') {
    try {
      await stato.mutateAsync(azione);
      setDialogo(null);
      toast.mostra({ variant: 'success', title: azione === 'pubblica' ? t.pubblicato : t.chiuso });
    } catch (e) {
      toast.mostra({ variant: 'error', title: e instanceof Error ? e.message : comuni.erroreGenerico });
    }
  }

  return (
    <>
      <IntestazioneAdmin
        sopra={<LinkIndietro to="/admin" testo={admin.percorsi.titolo} />}
        titolo={
          <span className="inline-flex flex-wrap items-center gap-3">
            {p.titolo} <BadgePercorso stato={p.stato} />
          </span>
        }
        sottotitolo={`${p.anno} · ${p.organizzatoreEmail}`}
        azione={
          <div className="flex flex-wrap gap-3">
            <Button icon={Pencil} onClick={() => navigate(`/admin/percorsi/${p.id}/modifica`)}>
              {admin.comuni.modifica}
            </Button>
            {p.stato === 'bozza' && (
              <Button variant="primary" icon={Send} onClick={() => setDialogo('pubblica')}>
                {t.pubblica}
              </Button>
            )}
            {p.stato === 'pubblicato' && (
              <Button icon={Flag} onClick={() => setDialogo('chiudi')}>
                {t.chiudi}
              </Button>
            )}
          </div>
        }
      />
      {p.iscrizioneAperta && (
        <Card className="flex flex-col gap-2">
          <p className="t-label inline-flex items-center gap-2">
            <Link2 aria-hidden size={20} strokeWidth={1.75} />
            {t.linkPubblico}
          </p>
          <p className="t-body-small text-ink-muted">{t.linkPubblicoAiuto}</p>
          <div className="flex flex-wrap items-center gap-3">
            <code className="t-code bg-surface-sunken rounded-md px-3 py-2 break-all">{linkPubblico}</code>
            <BottoneCopia testo={linkPubblico} etichetta={admin.comuni.copia} onCopiato={() => toast.mostra({ variant: 'success', title: admin.comuni.copiato })} />
          </div>
        </Card>
      )}
      <Tabs
        ariaLabel={p.titolo}
        value={scheda}
        onValueChange={(v) => setParams({ scheda: v }, { replace: true })}
        items={[
          { value: 'webinar', label: t.schede.webinar, count: p.numeroWebinar },
          { value: 'iscritti', label: t.schede.iscritti, count: p.numeroIscritti },
          { value: 'import', label: t.schede.import },
        ]}
      >
        <TabPanel value="webinar">
          <SchedaWebinar percorsoId={p.id} />
        </TabPanel>
        <TabPanel value="iscritti">
          <SchedaIscritti percorsoId={p.id} />
        </TabPanel>
        <TabPanel value="import">
          <SchedaImport percorsoId={p.id} onVaiAgliIscritti={() => setParams({ scheda: 'iscritti' })} />
        </TabPanel>
      </Tabs>
      <Dialog
        open={dialogo !== null}
        onOpenChange={(o) => !o && setDialogo(null)}
        title={dialogo === 'chiudi' ? t.chiudi : t.pubblica}
        description={dialogo === 'chiudi' ? t.chiudiTesto : t.pubblicaTesto}
        actions={
          <>
            <Button onClick={() => setDialogo(null)}>{admin.comuni.annulla}</Button>
            <Button variant="primary" onClick={() => dialogo && cambiaStato(dialogo)} loading={stato.isPending} loadingText={admin.comuni.inCorso}>
              {dialogo === 'chiudi' ? t.chiudi : t.pubblica}
            </Button>
          </>
        }
      />
    </>
  );
}

function SchedaWebinar({ percorsoId }: { percorsoId: string }) {
  const t = admin.percorsi.dettaglio;
  const tw = admin.webinar;
  const navigate = useNavigate();
  const tutti = useWebinarAdmin();
  const webinar = useMemo(() => (tutti.data ?? []).filter((w) => w.percorsoId === percorsoId), [tutti.data, percorsoId]);
  const colonne = useMemo<ColumnDef<Webinar, unknown>[]>(
    () => [
      { header: tw.colonne.quando, cell: ({ row }) => formattaIntervalloBreve(row.original.inizioPrevisto, row.original.finePrevista) },
      { header: tw.colonne.titolo, cell: ({ row }) => <Link to={`/admin/webinar/${row.original.id}`}>{row.original.titolo}</Link> },
      { header: tw.colonne.stato, cell: ({ row }) => <BadgeWebinar stato={row.original.stato} /> },
      { header: tw.colonne.confermati, cell: ({ row }) => tw.confermati(row.original.conteggi.confermatiOnline, row.original.conteggi.confermatiSala) },
      { header: tw.colonne.ingresso, cell: ({ row }) => admin.modalitaIngresso[row.original.modalitaIngresso] },
    ],
    [tw],
  );
  return (
    <>
      <div className="flex justify-end">
        <Button icon={CalendarPlus} onClick={() => navigate(`/admin/webinar/nuovo?percorso=${percorsoId}`)}>
          {t.nuovoWebinar}
        </Button>
      </div>
      {tutti.isError && <Alert variant="error" title={tutti.error.message} />}
      <DataTable
        caption={t.schede.webinar}
        columns={colonne}
        data={webinar}
        getRowId={(w) => w.id}
        loading={tutti.isPending}
        testi={admin.comuni}
        empty={
          <EmptyState icon={CalendarPlus} title={t.nessunWebinarTitolo}>
            {t.nessunWebinarTesto}
          </EmptyState>
        }
      />
    </>
  );
}

function SchedaIscritti({ percorsoId }: { percorsoId: string }) {
  const t = admin.percorsi.iscritti;
  const toast = useToast();
  const [q, setQ] = useState('');
  const [statoFiltro, setStatoFiltro] = useState('');
  const [pagina, setPagina] = useState(1);
  const iscritti = useIscritti(percorsoId, { q: q || undefined, stato: statoFiltro || undefined, pagina, perPagina: 50 });
  const aggiungi = useAggiungiIscritto(percorsoId);
  const annulla = useAnnullaIscrizione(percorsoId);
  const [dialogoAggiungi, setDialogoAggiungi] = useState(false);
  const [emailNuovo, setEmailNuovo] = useState('');
  const [erroreAggiungi, setErroreAggiungi] = useState<string | null>(null);
  const [daAnnullare, setDaAnnullare] = useState<Iscritto | null>(null);

  const colonne = useMemo<ColumnDef<Iscritto, unknown>[]>(
    () => [
      { header: t.colonne.nome, cell: ({ row }) => `${row.original.cognome} ${row.original.nome}` },
      { header: t.colonne.email, accessorKey: 'email' },
      { header: t.colonne.codiceFiscale, cell: ({ row }) => <span className="t-code">{row.original.codiceFiscale}</span> },
      { header: t.colonne.ordine, cell: ({ row }) => row.original.ordine ?? '—' },
      {
        header: t.colonne.stato,
        cell: ({ row }) =>
          row.original.statoIscrizione === 'annullata' ? (
            <Badge variant="error" icon={CircleDashed}>
              {t.annulla}
            </Badge>
          ) : (
            <Badge variant={row.original.statoUtente === 'attivo' ? 'success' : 'warning'} icon={row.original.statoUtente === 'attivo' ? CircleCheck : CircleDashed}>
              {admin.statiUtente[row.original.statoUtente]}
            </Badge>
          ),
      },
      { header: t.colonne.origine, cell: ({ row }) => t.origini[row.original.origine] },
      { header: t.colonne.dal, cell: ({ row }) => formattaDataBreve(row.original.iscrittoDal) },
      {
        id: 'azioni',
        header: '',
        cell: ({ row }) =>
          row.original.statoIscrizione === 'attiva' && (
            <Button variant="discreet" onClick={() => setDaAnnullare(row.original)}>
              {t.annulla}
            </Button>
          ),
      },
    ],
    [t],
  );

  async function confermaAggiungi() {
    setErroreAggiungi(null);
    try {
      await aggiungi.mutateAsync(emailNuovo.trim());
      setDialogoAggiungi(false);
      setEmailNuovo('');
      toast.mostra({ variant: 'success', title: t.aggiunto });
    } catch (e) {
      setErroreAggiungi(e instanceof Error ? e.message : comuni.erroreGenerico);
    }
  }
  async function confermaAnnulla() {
    if (!daAnnullare) return;
    try {
      await annulla.mutateAsync(daAnnullare.iscrizioneId);
      setDaAnnullare(null);
      toast.mostra({ variant: 'success', title: t.annullata });
    } catch (e) {
      toast.mostra({ variant: 'error', title: e instanceof Error ? e.message : comuni.erroreGenerico });
    }
  }

  const vuoto = !q && !statoFiltro;
  return (
    <>
      <Alert variant="info" title={t.avvisoCf} />
      <div className="grid grid-cols-1 md:grid-cols-[1fr_200px_auto] gap-4 items-end">
        <CampoRicerca valore={q} onCambia={(v) => { setQ(v); setPagina(1); }} hint={admin.comuni.cercaAiuto} />
        <NativeSelect
          label={admin.comuni.filtroStato}
          placeholder={admin.comuni.tutti}
          value={statoFiltro}
          onChange={(e) => { setStatoFiltro(e.target.value); setPagina(1); }}
          options={[
            { value: 'attiva', label: 'Attive' },
            { value: 'annullata', label: 'Annullate' },
          ]}
        />
        <Button icon={UserPlus} onClick={() => setDialogoAggiungi(true)}>
          {t.aggiungi}
        </Button>
        <Button icon={Download} onClick={() => window.open(urlEsportaIscritti(percorsoId), '_blank', 'noopener')}>
          {amministrazione.utente.esportaCsv}
        </Button>
      </div>
      {iscritti.isError && <Alert variant="error" title={iscritti.error.message} />}
      <DataTable
        caption={t.titolo}
        columns={colonne}
        data={iscritti.data?.elementi ?? []}
        getRowId={(i) => i.iscrizioneId}
        loading={iscritti.isPending}
        testi={admin.comuni}
        pagination={iscritti.data ? { pagina: iscritti.data.pagina, perPagina: iscritti.data.perPagina, totale: iscritti.data.totale, onPagina: setPagina } : undefined}
        empty={
          <EmptyState icon={Users} title={vuoto ? t.nessunoTitolo : t.nessunRisultato}>
            {vuoto ? t.nessunoTesto : null}
          </EmptyState>
        }
      />
      <Dialog
        open={dialogoAggiungi}
        onOpenChange={setDialogoAggiungi}
        title={t.aggiungiTitolo}
        description={t.aggiungiTesto}
        actions={
          <>
            <Button onClick={() => setDialogoAggiungi(false)}>{admin.comuni.annulla}</Button>
            <Button variant="primary" onClick={confermaAggiungi} loading={aggiungi.isPending} loadingText={admin.comuni.inCorso}>
              {t.aggiungiAzione}
            </Button>
          </>
        }
      >
        <TextField label={t.email} type="email" value={emailNuovo} onChange={(e) => setEmailNuovo(e.target.value)} error={erroreAggiungi ?? undefined} autoComplete="off" />
      </Dialog>
      <Dialog
        open={daAnnullare !== null}
        onOpenChange={(o) => !o && setDaAnnullare(null)}
        title={t.annullaTitolo}
        description={daAnnullare ? t.annullaTesto(`${daAnnullare.nome} ${daAnnullare.cognome}`) : ''}
        actions={
          <>
            <Button onClick={() => setDaAnnullare(null)}>{admin.comuni.annulla}</Button>
            <Button variant="primary" onClick={confermaAnnulla} loading={annulla.isPending} loadingText={admin.comuni.inCorso}>
              {t.annullaConferma}
            </Button>
          </>
        }
      />
    </>
  );
}

const MODELLO_CSV = 'codiceFiscale;nome;cognome;email;ordine;numeroAlbo\nRSSMRA85T10A562S;Mario;Rossi;mario.rossi@example.it;ODCEC Bologna;1234\n';

function SchedaImport({ percorsoId, onVaiAgliIscritti }: { percorsoId: string; onVaiAgliIscritti: () => void }) {
  const t = admin.percorsi.import;
  const carica = useCaricaImport(percorsoId);
  const conferma = useConfermaImport(percorsoId);
  const annulla = useAnnullaImport();
  const [file, setFile] = useState<File | null>(null);
  const [erroreFile, setErroreFile] = useState<string | null>(null);
  const [anteprima, setAnteprima] = useState<AnteprimaImport | null>(null);
  const [esito, setEsito] = useState<EsitoImport | null>(null);
  const passo = esito ? 3 : anteprima ? 2 : 1;

  function scegliFile(f: File) {
    setErroreFile(null);
    if (f.size > 5 * 1024 * 1024) return setErroreFile(t.fileTroppoGrande);
    if (!/\.(csv|xlsx)$/i.test(f.name)) return setErroreFile(t.fileNonSupportato);
    setFile(f);
  }
  async function caricaFile() {
    if (!file) return;
    setErroreFile(null);
    try {
      setAnteprima(await carica.mutateAsync(file));
    } catch (e) {
      setErroreFile(e instanceof Error ? e.message : comuni.erroreGenerico);
    }
  }
  async function confermaImport() {
    if (!anteprima) return;
    setEsito(await conferma.mutateAsync(anteprima.importId));
  }
  async function ricomincia() {
    if (anteprima && !esito) await annulla.mutateAsync(anteprima.importId).catch(() => undefined);
    setFile(null);
    setAnteprima(null);
    setEsito(null);
  }
  function scaricaModello() {
    const blob = new Blob([MODELLO_CSV], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'iscritti-modello.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  const colonneErrori: ColumnDef<AnteprimaImport['errori'][number], unknown>[] = [
    { header: t.colonneErrori.riga, accessorKey: 'riga' },
    { header: t.colonneErrori.campo, cell: ({ row }) => row.original.campo ?? '—' },
    { header: t.colonneErrori.messaggio, accessorKey: 'messaggio' },
  ];
  const colonneEsempio: ColumnDef<AnteprimaImport['esempio'][number], unknown>[] = [
    { header: '#', accessorKey: 'riga' },
    { header: admin.percorsi.iscritti.colonne.nome, cell: ({ row }) => `${row.original.cognome} ${row.original.nome}` },
    { header: admin.percorsi.iscritti.colonne.email, accessorKey: 'email' },
    { header: admin.percorsi.iscritti.colonne.codiceFiscale, cell: ({ row }) => <span className="t-code">{row.original.codiceFiscale}</span> },
    { header: admin.percorsi.iscritti.colonne.stato, cell: ({ row }) => t.esiti[row.original.esito] },
  ];

  return (
    <>
      <ol className="flex flex-wrap gap-4" aria-label={t.titolo}>
        {t.passi.map((p, i) => (
          <li key={p} className={`t-label inline-flex items-center gap-2 ${i + 1 === passo ? 'text-ink' : 'text-ink-muted'}`} aria-current={i + 1 === passo ? 'step' : undefined}>
            <span className={`h-7 w-7 rounded-full inline-flex items-center justify-center t-caption ${i + 1 <= passo ? 'bg-accent text-on-accent' : 'bg-surface-sunken'}`}>{i + 1}</span>
            {p}
          </li>
        ))}
      </ol>

      {passo === 1 && (
        <div className="flex flex-col gap-6 max-w-[640px]">
          <FileDrop label={t.file} hint={t.fileAiuto} accept=".csv,.xlsx" onFile={scegliFile} file={file} error={erroreFile ?? undefined} testoScegli={t.scegli} />
          <div className="flex flex-wrap gap-3 items-center">
            <Button variant="primary" onClick={caricaFile} disabled={!file} loading={carica.isPending} loadingText={t.caricamento}>
              {t.carica}
            </Button>
            <Button variant="discreet" icon={Download} onClick={scaricaModello}>
              {t.modello}
            </Button>
          </div>
        </div>
      )}

      {passo === 2 && anteprima && (
        <div className="flex flex-col gap-6">
          <Card className="flex flex-col gap-3">
            <h3 className="t-title-3">{t.anteprimaTitolo}</h3>
            <ul className="flex flex-wrap gap-2">
              <li><Badge variant="neutral">{t.righeTotali(anteprima.righeTotali)}</Badge></li>
              <li><Badge variant="success" icon={CircleCheck}>{t.righeValide(anteprima.righeValide)}</Badge></li>
              <li><Badge variant={anteprima.righeErrate > 0 ? 'error' : 'neutral'}>{t.righeErrate(anteprima.righeErrate)}</Badge></li>
              <li><Badge variant="accent" icon={Plus}>{t.nuoviUtenti(anteprima.nuoviUtenti)}</Badge></li>
              <li><Badge variant="neutral">{t.utentiEsistenti(anteprima.utentiEsistenti)}</Badge></li>
              <li><Badge variant="neutral">{t.giaIscritti(anteprima.giaIscritti)}</Badge></li>
            </ul>
          </Card>
          {anteprima.errori.length > 0 && (
            <div className="flex flex-col gap-3">
              <Alert variant="warning" title={t.erroriTitolo}>{t.erroriTesto}</Alert>
              <DataTable caption={t.erroriTitolo} columns={colonneErrori} data={anteprima.errori} getRowId={(e) => `${e.riga}-${e.campo}`} empty={null} testi={admin.comuni} />
            </div>
          )}
          {anteprima.esempio.length > 0 && (
            <div className="flex flex-col gap-3">
              <h3 className="t-title-3">{t.esempioTitolo}</h3>
              <DataTable caption={t.esempioTitolo} columns={colonneEsempio} data={anteprima.esempio} getRowId={(r) => String(r.riga)} empty={null} testi={admin.comuni} />
            </div>
          )}
          {anteprima.righeValide === 0 ? (
            <Alert variant="error" title={t.nessunaRigaValida} action={<Button onClick={ricomincia}>{t.annullaImport}</Button>} />
          ) : (
            <Card className="flex flex-col gap-4">
              <p className="t-body">{t.riepilogo(anteprima.nuoviUtenti, anteprima.nuoviUtenti)}</p>
              {anteprima.utentiEsistenti > 0 && <p className="t-body-small text-ink-muted">{t.riepilogoEsistenti(anteprima.utentiEsistenti)}</p>}
              {conferma.isError && <Alert variant="error" title={conferma.error.message} />}
              <div className="flex flex-wrap gap-3">
                <Button variant="primary" onClick={confermaImport} loading={conferma.isPending} loadingText={t.confermaInCorso}>
                  {t.conferma}
                </Button>
                <Button onClick={ricomincia}>{t.annullaImport}</Button>
              </div>
            </Card>
          )}
        </div>
      )}

      {passo === 3 && esito && (
        <Outcome
          variant="success"
          title={t.esitoTitolo}
          action={
            <div className="flex flex-wrap gap-3">
              <Button variant="primary" onClick={onVaiAgliIscritti}>{t.vaiAgliIscritti}</Button>
              <Button onClick={ricomincia}>{t.nuovoImport}</Button>
            </div>
          }
        >
          {t.esito(esito)}
        </Outcome>
      )}
    </>
  );
}
